import { Point, Feature, MultiPolygon, LineString } from "geojson";
import * as turf from "@turf/turf";

export const degreeInSector = (
    start: number,
    stop: number,
    degree: number,
): boolean => {
    if (start < stop) {
        return degree > start && degree < stop;
    }
    return degree > start || degree < stop;
};

export const sectorsOverlap = (
    start1: number,
    stop1: number,
    start2: number,
    stop2: number,
): boolean => {
    return (
        degreeInSector(start1, stop1, start2) ||
        degreeInSector(start1, stop1, stop2)
    );
};

export const sectorContainsSector = (
    start1: number,
    stop1: number,
    start2: number,
    stop2: number,
): boolean => {
    return (
        degreeInSector(start1, stop1, start2) &&
        degreeInSector(start1, stop1, stop2)
    );
};

export const decrementDegree = (
    degree: number,
    step: number = 1,
    startDegree: number = 0,
    stopDegree: number = 360,
): number => {
    const newDegree = degree - step;
    if (startDegree > stopDegree) {
        if (newDegree < stopDegree && newDegree > startDegree)
            return stopDegree - newDegree + startDegree;
    } else if (newDegree < startDegree)
        return stopDegree - Math.abs(newDegree) + startDegree;
    return newDegree;
};

export const incrementDegree = (
    degree: number,
    step: number = 1,
    startDegree: number = 0,
    stopDegree: number = 360,
): number => {
    const newDegree = degree + step;
    if (startDegree > stopDegree) {
        if (newDegree > stopDegree && newDegree < startDegree)
            return startDegree + newDegree - stopDegree;
    } else if (newDegree > stopDegree)
        return startDegree + newDegree - stopDegree;
    return newDegree % 360;
};

export const getSectorGeometry = (
    sectorStart: number,
    sectorStop: number,
    centerPoint: Point,
    length: number,
    fieldBoundary?: Feature<MultiPolygon> | null,
    units: "feet" | "meters" = "feet",
): Feature<MultiPolygon> => {
    const sector = turf.sector(centerPoint, length, sectorStart, sectorStop, {
        units: units,
    });
    if (fieldBoundary) {
        return turf.intersect({
            type: "FeatureCollection",
            features: [sector, fieldBoundary],
        }) as Feature<MultiPolygon>;
    }
    return turf.multiPolygon([
        sector.geometry.coordinates,
    ]) as Feature<MultiPolygon>;
};

export const getSectorOutline = (
    sectorStart: number,
    sectorStop: number,
    centerPoint: Point,
    length: number,
    units: "feet" | "meters" = "feet",
): Feature<LineString> => {
    const sector = turf.sector(centerPoint, length, sectorStart, sectorStop, {
        units: units,
    });
    const outline = turf.polygonToLine(sector) as Feature<LineString>;
    if (Number(sectorStart) !== 0 || ![0, 360].includes(Number(sectorStop))) {
        outline.geometry.coordinates = outline.geometry.coordinates.slice(
            1,
            -1,
        );
    }
    return outline;
};
